import { useRef, useState, type FormEvent } from 'react';
import { Loader2, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useTaskComments,
  useAddTaskComment,
  useDeleteTaskComment,
  useActiveCollaborators,
} from '../../hooks/queries/useTaskCollaboration';
import { useAuthStore } from '../../store/useAuthStore';
import { Avatar } from '../ui/Avatar';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import { MentionAutocomplete } from '../messaging/MentionAutocomplete';

interface TaskCommentsSectionProps {
  taskId: string;
}

export function TaskCommentsSection({ taskId }: TaskCommentsSectionProps) {
  const { data: comments = [], isLoading } = useTaskComments(taskId);
  const { data: collaborators = [] } = useActiveCollaborators();
  const addMut = useAddTaskComment();
  const deleteMut = useDeleteTaskComment(taskId);
  const me = useAuthStore((s) => s.collaborator);
  const isAdmin = me?.role === 'admin' || me?.role === 'secretary';

  const [content, setContent] = useState('');
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) return;
    addMut.mutate(
      { taskId, content: trimmed },
      {
        onSuccess: () => setContent(''),
        onError: (err) => toast.error(`Commentaire impossible : ${err.message}`),
      },
    );
  }

  function handleDelete(commentId: string) {
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    deleteMut.mutate(commentId, {
      onError: (err) => toast.error(`Suppression impossible : ${err.message}`),
    });
  }

  return (
    <section>
      <h3 className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide mb-2">
        Commentaires
        {comments.length > 0 && (
          <span className="ml-1.5 font-normal">({comments.length})</span>
        )}
      </h3>

      <div className="space-y-2.5 mb-3">
        {isLoading && (
          <div className="text-xs text-cime-text-muted flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" />
            Chargement…
          </div>
        )}
        {!isLoading && comments.length === 0 && (
          <p className="text-xs text-cime-text-muted italic">
            Aucun commentaire pour l'instant.
          </p>
        )}
        {comments.map((c) => {
          const canDelete = c.author_id === me?.id || isAdmin;
          return (
            <article key={c.id} className="flex gap-2 group">
              <CollaboratorLink collaboratorId={c.author_id} title={c.author?.full_name ? `Voir le profil de ${c.author.full_name}` : undefined}>
                <Avatar
                  name={c.author?.full_name ?? 'Inconnu'}
                  src={c.author?.avatar_url}
                  size="xs"
                />
              </CollaboratorLink>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <CollaboratorLink collaboratorId={c.author_id} title={c.author?.full_name ? `Voir le profil de ${c.author.full_name}` : undefined}>
                    <span className="text-xs font-semibold text-cime-text-primary">
                      {c.author?.full_name ?? 'Inconnu'}
                    </span>
                  </CollaboratorLink>
                  <span className="text-[11px] text-cime-text-muted tabular-nums">
                    {new Date(c.created_at).toLocaleString('fr-FR', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => handleDelete(c.id)}
                      className="opacity-0 group-hover:opacity-100 transition text-neutral-400 hover:text-red-600 ml-auto"
                      aria-label="Supprimer le commentaire"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <p className="text-sm text-cime-text-primary whitespace-pre-wrap mt-0.5">
                  {c.content}
                </p>
              </div>
            </article>
          );
        })}
      </div>

      <form
        onSubmit={handleSubmit}
        className="relative flex items-end gap-2 border border-neutral-200 rounded-md px-2 py-1.5 focus-within:border-cime-primary"
      >
        {mentionQuery !== null && (
          <MentionAutocomplete
            query={mentionQuery}
            collaborators={collaborators}
            onSelect={(c) => {
              if (!textareaRef.current) return;
              const cursorPos = textareaRef.current.selectionStart ?? content.length;
              const replaced = content
                .slice(0, cursorPos)
                .replace(/@[\wÀ-ɏ\s]*$/, `@${c.full_name} `);
              const next = replaced + content.slice(cursorPos);
              setContent(next);
              setMentionQuery(null);
              setTimeout(() => {
                textareaRef.current?.focus();
                textareaRef.current?.setSelectionRange(replaced.length, replaced.length);
              }, 0);
            }}
            onClose={() => setMentionQuery(null)}
          />
        )}
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => {
            const text = e.target.value;
            setContent(text);
            const cursorPos = e.target.selectionStart ?? text.length;
            const before = text.slice(0, cursorPos);
            const m = before.match(/@([\w\sÀ-ɏ]*)$/);
            setMentionQuery(m ? (m[1] ?? '') : null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setMentionQuery(null);
            }
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmit(e as unknown as FormEvent);
            }
          }}
          rows={2}
          placeholder="Écrire un commentaire… (@ pour mentionner, Cmd+Enter pour envoyer)"
          className="flex-1 text-sm bg-transparent border-0 outline-none resize-none placeholder-cime-text-muted"
        />
        <button
          type="submit"
          disabled={!content.trim() || addMut.isPending}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-cime-primary text-white hover:bg-cime-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition"
          aria-label="Envoyer"
        >
          {addMut.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </button>
      </form>
    </section>
  );
}
